/**
 * The learner's own marks on the exemplar.
 *
 * Marking IS the exercise: the learner reads the vignette clean, then selects
 * passages and tags each one green ("what the leader did well") or yellow ("what
 * worked, but cost something"). The authored spans are an answer key that is only
 * rendered afterwards, beside what the learner marked.
 *
 * A mark is stored as offsets into the ORIGINAL paragraph string, never as a DOM
 * reference, because the paragraph is re-split on every render. Selections that
 * cross paragraphs are clamped to the paragraph they started in — a mark that
 * spans two paragraphs has no honest offset pair.
 */

import type { DojoHighlightKind, DojoHighlightSpan, DojoLearnerMark } from '@/types/dojo';
import { trimRange, type HighlightPlacement } from './highlightSpans';

/** A resolved but not yet committed selection. */
export interface LearnerMarkRange {
  paragraph: number;
  start: number;
  end: number;
}

/** One paragraph's rendered root, for mapping a DOM selection back to offsets. */
export interface ParagraphElement {
  index: number;
  element: HTMLElement;
}

export interface MarkedSegment {
  text: string;
  /** The learner's mark covering this stretch, if any. */
  mark: DojoLearnerMark | null;
  /** The authored span covering this stretch — comparison view only. */
  span: DojoHighlightSpan | null;
  /** True while this stretch is a selection awaiting a colour. */
  pending: boolean;
}

export interface MarkedParagraph {
  index: number;
  segments: MarkedSegment[];
}

export interface MarkComparison {
  /** Key spans the learner marked. */
  matchedSpanIds: string[];
  /** Key spans the learner walked past — the whole point of the comparison. */
  missedSpanIds: string[];
  /** Learner marks that landed on a key span. */
  matchedMarkIds: string[];
  /** Learner marks the key says nothing about. */
  extraMarkIds: string[];
}

// The learner's two choices, in their language — never "correct / incorrect".
export const LEARNER_MARK_KIND_LABEL: Record<DojoHighlightKind, string> = {
  admirable: 'Did well',
  adequate: 'Could have gone further',
};

export const LEARNER_MARK_KINDS: DojoHighlightKind[] = ['admirable', 'adequate'];

let markSequence = 0;

/** Client-side id. Marks are identified by the server only through their offsets. */
export function createMarkId(): string {
  markSequence += 1;
  return `mark-${Date.now().toString(36)}-${markSequence}`;
}

/**
 * Clamp a raw offset pair onto the paragraph and drop whitespace off both ends.
 * Returns null when nothing but whitespace was selected.
 */
export function normalizeMarkRange(
  paragraphText: string,
  start: number,
  end: number
): { start: number; end: number } | null {
  if (typeof paragraphText !== 'string') return null;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  const low = Math.max(0, Math.min(Math.min(start, end), paragraphText.length));
  const high = Math.min(paragraphText.length, Math.max(Math.max(start, end), 0));
  if (high <= low) return null;

  const [trimmedStart, trimmedEnd] = trimRange(paragraphText, low, high);
  return trimmedEnd > trimmedStart ? { start: trimmedStart, end: trimmedEnd } : null;
}

/** Build a mark from a raw range, taking the quote from the original text. */
export function createLearnerMark(
  paragraphs: string[],
  range: LearnerMarkRange,
  kind: DojoHighlightKind
): DojoLearnerMark | null {
  const paragraphText = paragraphs[range.paragraph];
  if (typeof paragraphText !== 'string') return null;

  const normalized = normalizeMarkRange(paragraphText, range.start, range.end);
  if (!normalized) return null;

  return {
    id: createMarkId(),
    paragraph: range.paragraph,
    start: normalized.start,
    end: normalized.end,
    quote: paragraphText.slice(normalized.start, normalized.end),
    kind,
  };
}

function overlaps(
  a: { paragraph: number; start: number; end: number },
  b: { paragraph: number; start: number; end: number }
): boolean {
  return a.paragraph === b.paragraph && a.start < b.end && b.start < a.end;
}

/** Every existing mark the range would collide with. */
export function findOverlappingMarks(
  marks: DojoLearnerMark[],
  range: LearnerMarkRange
): DojoLearnerMark[] {
  return marks.filter((mark) => overlaps(mark, range));
}

function sortMarks(marks: DojoLearnerMark[]): DojoLearnerMark[] {
  return [...marks].sort((a, b) => a.paragraph - b.paragraph || a.start - b.start);
}

/**
 * Add a mark, replacing anything it overlaps.
 *
 * Nested or partially-overlapping ranges have no sane rendering and no sane
 * offset story, so the newer intent wins outright.
 */
export function upsertMark(
  marks: DojoLearnerMark[],
  mark: DojoLearnerMark
): DojoLearnerMark[] {
  const kept = marks.filter((existing) => existing.id !== mark.id && !overlaps(existing, mark));
  return sortMarks([...kept, mark]);
}

export function removeMark(marks: DojoLearnerMark[], id: string): DojoLearnerMark[] {
  return marks.filter((mark) => mark.id !== id);
}

export function setMarkKind(
  marks: DojoLearnerMark[],
  id: string,
  kind: DojoHighlightKind
): DojoLearnerMark[] {
  return marks.map((mark) => (mark.id === id ? { ...mark, kind } : mark));
}

/**
 * Character offset of a DOM position inside `root`.
 *
 * A Range over the leading content is the reliable way to do this: it counts the
 * same characters the browser would, whatever elements the segments are wrapped
 * in, and it accepts element-plus-child-index positions as well as text nodes.
 */
export function offsetInElement(root: HTMLElement, node: Node, nodeOffset: number): number | null {
  if (!root.contains(node)) return null;
  try {
    const range = root.ownerDocument.createRange();
    range.selectNodeContents(root);
    range.setEnd(node, nodeOffset);
    return range.toString().length;
  } catch {
    return null;
  }
}

/**
 * Map a DOM selection onto one paragraph's offsets.
 *
 * A selection that runs out of the paragraph it started in is clamped to that
 * paragraph's end rather than being read across the boundary, where the offsets
 * would belong to two different strings. A selection that starts outside the
 * prose entirely is ignored.
 */
export function resolveSelectionRange(
  range: Range,
  paragraphs: ParagraphElement[]
): LearnerMarkRange | null {
  const startParagraph = paragraphs.find((paragraph) =>
    paragraph.element.contains(range.startContainer)
  );
  if (!startParagraph) return null;

  const start = offsetInElement(startParagraph.element, range.startContainer, range.startOffset);
  if (start === null) return null;

  const endsInSameParagraph = startParagraph.element.contains(range.endContainer);
  const end = endsInSameParagraph
    ? offsetInElement(startParagraph.element, range.endContainer, range.endOffset)
    : (startParagraph.element.textContent ?? '').length;
  if (end === null || end <= start) return null;

  return { paragraph: startParagraph.index, start, end };
}

interface Cover {
  start: number;
  end: number;
}

function covering<T extends Cover>(ranges: T[], start: number, end: number): T | undefined {
  return ranges.find((range) => range.start <= start && range.end >= end);
}

/**
 * Split every paragraph on the union of all range boundaries.
 *
 * Learner marks and authored spans are independent layers that may overlap each
 * other, so segments are cut at every boundary and each one reports which layers
 * cover it. That is what lets one stretch render as "you marked it and so did the
 * key" and the next as "the key marked it and you did not".
 */
export function buildMarkedParagraphs(
  paragraphs: string[],
  marks: DojoLearnerMark[],
  placements: HighlightPlacement[] = [],
  pending: LearnerMarkRange | null = null
): MarkedParagraph[] {
  return paragraphs.map((paragraphText, index) => {
    const markRanges = marks.filter((mark) => mark.paragraph === index);
    const spanRanges = placements.filter((placement) => placement.paragraph === index);
    const pendingRange = pending && pending.paragraph === index ? pending : null;

    const boundaries = new Set<number>([0, paragraphText.length]);
    for (const range of [...markRanges, ...spanRanges, ...(pendingRange ? [pendingRange] : [])]) {
      boundaries.add(Math.max(0, Math.min(range.start, paragraphText.length)));
      boundaries.add(Math.max(0, Math.min(range.end, paragraphText.length)));
    }

    const points = Array.from(boundaries).sort((a, b) => a - b);
    const segments: MarkedSegment[] = [];

    for (let i = 0; i < points.length - 1; i += 1) {
      const start = points[i];
      const end = points[i + 1];
      if (end <= start) continue;
      segments.push({
        text: paragraphText.slice(start, end),
        mark: covering(markRanges, start, end) ?? null,
        span: covering(spanRanges, start, end)?.span ?? null,
        pending: Boolean(pendingRange && pendingRange.start <= start && pendingRange.end >= end),
      });
    }

    if (segments.length === 0) {
      segments.push({ text: paragraphText, mark: null, span: null, pending: false });
    }

    return { index, segments };
  });
}

/** Three-way read of the learner's marks against the answer key. */
export function compareMarksToKey(
  marks: DojoLearnerMark[],
  placements: HighlightPlacement[]
): MarkComparison {
  const matchedSpanIds: string[] = [];
  const missedSpanIds: string[] = [];
  const matchedMarkIds = new Set<string>();

  for (const placement of placements) {
    const hits = marks.filter((mark) => overlaps(mark, placement));
    if (hits.length > 0) {
      matchedSpanIds.push(placement.span.id);
      hits.forEach((mark) => matchedMarkIds.add(mark.id));
    } else {
      missedSpanIds.push(placement.span.id);
    }
  }

  return {
    matchedSpanIds,
    missedSpanIds,
    matchedMarkIds: marks.filter((mark) => matchedMarkIds.has(mark.id)).map((mark) => mark.id),
    extraMarkIds: marks.filter((mark) => !matchedMarkIds.has(mark.id)).map((mark) => mark.id),
  };
}
